import React, {createContext, ReactNode, useContext} from 'react';
import {useQuery} from '@tanstack/react-query';
import baseUrl from '../api/baseUrl';

declare global {
    interface Window {
        __APP_SETTINGS__?: unknown;
    }
}

export type AppSettingsData = {
    isLoading: boolean;
    isDatabaseAvailable: boolean;
    databaseErrorMessage: string | null;
    fromEmailAddress: string;
    defaultPersonId: string;
};

const emptyAppSettingsData: AppSettingsData = {
    isLoading: true,
    isDatabaseAvailable: true,
    databaseErrorMessage: null,
    fromEmailAddress: '',
    defaultPersonId: ''
};

// Fallback when the settings were not embedded in index.html (Vite dev server).
const fetchAppSettings = async (): Promise<AppSettingsData> => {
    const response = await fetch(`${baseUrl}/api/app-settings`);
    if (!response.ok) {
        throw new Error('Network response was not ok');
    }
    return response.json();
};

const mergeEmbeddedSettings = (raw: unknown): AppSettingsData | undefined => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return undefined;
    }
    return {
        ...emptyAppSettingsData,
        ...(raw as Partial<AppSettingsData>),
        isLoading: false
    };
};

const embeddedSettings = mergeEmbeddedSettings(window.__APP_SETTINGS__);

const AppSettingsContext = createContext<AppSettingsData>(emptyAppSettingsData);

export const AppSettingsProvider: React.FC<{children: ReactNode}> = ({
    children
}) => {
    const {data, error} = useQuery({
        queryKey: ['appSettings'],
        queryFn: fetchAppSettings,
        staleTime: Infinity,
        initialData: embeddedSettings
    });

    const appSettingsData = data ?? emptyAppSettingsData;

    const showDatabaseUnavailableBanner =
        !appSettingsData.isLoading && !appSettingsData.isDatabaseAvailable;

    return (
        <AppSettingsContext.Provider value={appSettingsData}>
            {!!error && (
                <div
                    className="alert alert-warning text-center mb-0 rounded-0"
                    role="alert"
                >
                    Some settings could not be loaded. Refreshing the browser
                    may help.
                </div>
            )}
            {showDatabaseUnavailableBanner && (
                <div
                    className="alert alert-warning text-center mb-0 rounded-0"
                    role="alert"
                >
                    The database is currently unavailable, so data may not
                    load or save.
                    {appSettingsData.databaseErrorMessage && (
                        <> {appSettingsData.databaseErrorMessage}</>
                    )}
                </div>
            )}
            {children}
        </AppSettingsContext.Provider>
    );
};

const useAppSettings = () => useContext(AppSettingsContext);

// eslint-disable-next-line react-refresh/only-export-components
export default useAppSettings;
